import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  CreditCard,
  Truck,
  Zap,
  Package,
  Bell,
  RotateCcw,
  Search,
  Store,
  BarChart3,
  Shield,
  Smartphone,
  ShoppingBag,
  MapPin,
  Layers,
  Database,
  HardDrive,
  Settings,
  FileText,
  Home,
  Palette,
  Route,
  Wrench,
  Tag,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { FASH_LOGO } from '../constants/brand';
import { SHOPPER_HOME } from '../constants/routes';
import { BackButton } from '../components/ui/BackButton';

// ─── Types ──────────────────────────────────────────────────

interface ChangelogEntry {
  id: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  title: string;
  tag: 'new' | 'upgraded' | 'improved' | 'security' | 'fix';
  area: string;
  summary: string;
  details: string[];
  files: string[];
}

// ─── Tag config ─────────────────────────────────────────────

const tagStyles: Record<string, { bg: string; text: string; label: string }> = {
  new:      { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'New' },
  upgraded: { bg: 'bg-blue-100',    text: 'text-blue-700',    label: 'Upgraded' },
  improved: { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Improved' },
  security: { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Security' },
  fix:      { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Fix' },
};

// ─── All changelog entries ──────────────────────────────────

const changelog: ChangelogEntry[] = [
  {
    id: 'footer-nav-categories-sale-faq-returns',
    icon: Store,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    title: 'Footer & Navigation: Boutiques, Categories, Sale, FAQ, Returns',
    tag: 'improved',
    area: 'Shopper',
    summary: 'Footer and bottom nav links now go to the right pages: Boutiques lists all boutiques, Categories shows items by category, Sale shows sale-flagged items only, FAQ opens the FAQ page, and Returns scrolls to the return policy section.',
    details: [
      'Boutiques (footer and bottom Shop button): navigates to /boutiques and lists all available boutiques.',
      'Categories: new /categories page — choose a category (Dresses, Tops, Bottoms, etc.) to see all clothing items in that category, powered by search with category filter.',
      'Sale: new /sale page — shows only products explicitly marked with the sale flag (Algolia filter sale:true, Firestore filter sale === true).',
      'FAQ: footer "FAQs" link opens the Frequently Asked Questions page at /faq.',
      'Returns: footer "Returns" link goes to /faq#returns — scrolls to the "Returns & Disputes" section and briefly highlights it so the return policy is easy to find.',
      'Search (useSearch): added sale filter support; browse-by-filter mode so Categories and Sale pages can run search with only category or sale filter (no query required).',
    ],
    files: [
      'src/pages/shopper/HomePage.tsx — footer links updated',
      'src/pages/shopper/FAQPage.tsx — id="returns", scroll + highlight on #returns',
      'src/pages/shopper/BoutiquesListPage.tsx — Shop button navigates to /boutiques',
      'src/pages/shopper/CategoriesPage.tsx (NEW)',
      'src/pages/shopper/SalePage.tsx (NEW)',
      'src/hooks/useSearch.ts — sale filter, browse-by-filter',
      'src/router.tsx — routes /categories, /sale',
      'src/pages/ChangelogPage.tsx — this entry',
    ],
  },
  {
    id: 'stripe',
    icon: CreditCard,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    title: 'Stripe Payment System',
    tag: 'new',
    area: 'Payments',
    summary: 'Full payment integration so shoppers can pay for orders with a credit card. Uses "authorize only" so you\'re not charged until the courier picks up your items.',
    details: [
      'Businesses can connect their own Stripe account by pasting API keys. The system validates the keys with Stripe before saving them securely.',
      'When a shopper checks out, a PaymentIntent is created in "manual capture" mode. This means the card is authorized (funds held) but not charged yet.',
      'After the courier physically picks up the items from the boutique, the payment is automatically captured (charged for real).',
      'If an order is cancelled before pickup, the authorization is voided and the shopper\'s funds are released immediately.',
      'Refunds are processed automatically through Stripe when a return is approved.',
      'All sensitive Stripe keys are stored in a private Firestore subcollection that only Cloud Functions can access — never exposed to the frontend.',
    ],
    files: [
      'functions/src/services/stripe-service.ts (NEW)',
      'functions/src/index.ts — 4 new callable functions',
      'src/hooks/useStripePayment.ts (NEW)',
      'src/pages/shopper/BagPage.tsx — checkout rewrite',
    ],
  },
  {
    id: 'uber',
    icon: Truck,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    title: 'Uber Direct Delivery',
    tag: 'new',
    area: 'Delivery',
    summary: 'Real-time same-day delivery powered by Uber Direct. When a business approves an order, an Uber courier is automatically dispatched.',
    details: [
      'The system handles Uber\'s OAuth2 token management automatically — tokens are cached and refreshed before they expire.',
      'When an order reaches the "Dispatching Driver" state, the system calls Uber Direct to create a delivery. Uber finds a nearby courier.',
      'Uber sends real-time webhook updates as the delivery progresses: courier assigned, picked up, en route, delivered.',
      'Each Uber status automatically maps to a FASH order state — so the order timeline updates in real time without any manual intervention.',
      'Shoppers can see their courier\'s name, vehicle, and a live tracking URL directly on the order tracking page.',
      'If the Uber dispatch fails (no couriers available), the order is flagged for admin review and can be retried.',
      'Orders can be cancelled through Uber before the courier picks up the items.',
    ],
    files: [
      'functions/src/services/uber-direct.ts (NEW)',
      'functions/src/delivery/uber-delivery.ts (NEW)',
      'functions/src/index.ts — webhook endpoint + delivery functions',
      'src/hooks/useDelivery.ts (NEW)',
    ],
  },
  {
    id: 'triggers',
    icon: Zap,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    title: 'Order State Machine — Now Fully Wired',
    tag: 'upgraded',
    area: 'Backend',
    summary: 'Every order state transition now triggers real side effects. Previously these were all placeholder "TODO" stubs — now they actually do things.',
    details: [
      'INVENTORY_RESERVED: Actually locks inventory with a 15-minute expiration. If the shopper doesn\'t complete checkout in time, the items go back to available.',
      'PAYMENT_AUTHORIZED: Sends an order confirmation push notification to the shopper.',
      'PENDING_BUSINESS_APPROVAL: Sends a push and email notification to the business owner alerting them of a new order.',
      'DISPATCHING_DRIVER: Calls the Uber Direct API to dispatch a real courier.',
      'DRIVER_ASSIGNED: Notifies the shopper that a courier has been found and is heading to the boutique.',
      'PICKED_UP: This is the big one — captures the payment (actually charges the card) and permanently commits the inventory.',
      'OUT_FOR_DELIVERY: Sends a "your order is on the way" notification.',
      'DELIVERED: Sends delivery confirmation plus an email receipt.',
      'CANCELLED: Voids the payment authorization, releases all reserved inventory, and notifies all parties.',
      'REFUNDED: Processes the Stripe refund, restocks inventory, and sends confirmation.',
    ],
    files: [
      'functions/src/triggers/order-triggers.ts — rewritten (~500 lines added)',
    ],
  },
  {
    id: 'inventory',
    icon: Package,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    title: 'Inventory Management System',
    tag: 'new',
    area: 'Backend',
    summary: 'Transaction-safe inventory tracking that prevents overselling. If two shoppers try to buy the last item at the same time, only one will succeed.',
    details: [
      'All inventory operations run inside Firestore Transactions — this guarantees consistency even under high traffic.',
      'When a shopper starts checkout, their items are "reserved" with a 15-minute timer. Other shoppers will see those items as unavailable.',
      'If checkout isn\'t completed within 15 minutes, a scheduled Cloud Function automatically releases the reservation and the items become available again.',
      'When the courier picks up the order, reserved inventory is "committed" — permanently removed from stock.',
      'Cancellations automatically release reserved inventory back to available.',
      'Returns trigger automatic restocking — the inventory count goes back up.',
      'Low-stock detection alerts businesses when inventory drops below a configurable threshold.',
    ],
    files: [
      'functions/src/services/inventory-service.ts (NEW)',
      'functions/src/triggers/inventory-cleanup.ts (NEW)',
    ],
  },
  {
    id: 'notifications',
    icon: Bell,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    title: 'Push Notification System',
    tag: 'new',
    area: 'Notifications',
    summary: 'Multi-channel notification system with push notifications, email, and in-app messages. Users can customize exactly what notifications they receive.',
    details: [
      'Uses Firebase Cloud Messaging (FCM) for browser push notifications. Shoppers opt-in with a single tap.',
      'Email notifications powered by SendGrid — includes order confirmations, delivery updates, and receipts.',
      'Every notification also creates an in-app notification document, so users can always see their notification history.',
      'User preferences are fully customizable: toggle push, email, and SMS independently for each category.',
      'Categories include: order updates, delivery alerts, promotions, new arrivals, and security alerts.',
      'Quiet hours support — notifications are held during configured quiet periods and delivered afterwards.',
      'A beautiful new Notification Preferences page lets users control everything with toggle switches.',
    ],
    files: [
      'functions/src/services/notification-service.ts (NEW)',
      'src/config/firebase.ts — FCM setup',
      'src/hooks/useNotifications.ts (NEW)',
      'src/pages/shopper/NotificationPreferencesPage.tsx (NEW)',
    ],
  },
  {
    id: 'returns',
    icon: RotateCcw,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    title: 'Return & Refund System',
    tag: 'new',
    area: 'Orders',
    summary: 'Full return request lifecycle from request to refund. Shoppers can file returns, businesses can review them, and refunds are processed automatically through Stripe.',
    details: [
      'Shoppers can file a return for specific reasons: wrong item, damaged, not as described, changed mind, or never received.',
      'Return lifecycle flows through: requested, approved, item received, refund issued, closed.',
      'Businesses review return requests and can approve, partially approve, or deny them.',
      'Resolution options include: full refund, partial refund, exchange, store credit, or denied.',
      'When a return is approved, the Stripe refund is processed automatically — no manual payment handling needed.',
      'Approved returns also trigger automatic inventory restocking so the item becomes available for other shoppers.',
    ],
    files: [
      'functions/src/services/return-service.ts (NEW)',
      'functions/src/index.ts — fileReturn + resolveReturn callables',
    ],
  },
  {
    id: 'search',
    icon: Search,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    title: 'Algolia Product Search',
    tag: 'new',
    area: 'Search',
    summary: 'Instant, typo-tolerant product search powered by Algolia. Search as you type with a beautiful full-screen overlay, plus a dedicated results page with filters.',
    details: [
      'Algolia syncs automatically with Firestore — when a product is added, updated, or deleted, the search index updates in real time.',
      'The search overlay appears instantly on tap, showing recent searches and popular suggestions.',
      'Results appear as you type with ~250ms debounce for a smooth experience.',
      'The dedicated search results page has filters for category, brand, and price range, plus multiple sort options.',
      'If Algolia isn\'t configured (no API keys), the system falls back gracefully to basic Firestore text queries.',
      'Recent searches are saved in the browser\'s local storage so users can quickly repeat past searches.',
    ],
    files: [
      'functions/src/services/algolia-sync.ts (NEW)',
      'src/config/algolia.ts (NEW)',
      'src/hooks/useSearch.ts (NEW)',
      'src/components/shopper/SearchOverlay.tsx (NEW)',
      'src/pages/shopper/SearchResultsPage.tsx (NEW)',
    ],
  },
  {
    id: 'onboarding',
    icon: Store,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    title: 'Business Onboarding Wizard',
    tag: 'new',
    area: 'Business',
    summary: 'A guided 8-step setup flow for new businesses joining FASH. Covers everything from store identity to legal compliance, with progress saving so you can finish later.',
    details: [
      'Step 1 — Business Identity: store name, description, logo upload, cover image.',
      'Step 2 — Address: full physical address with optional map pin.',
      'Step 3 — Hours: set opening/closing times for each day of the week.',
      'Step 4 — Fulfillment: configure delivery options and pickup settings.',
      'Step 5 — Inventory: set up initial product catalog and stock levels.',
      'Step 6 — Legal: business registration details, tax information.',
      'Step 7 — Social: link social media profiles and website.',
      'Step 8 — Review: see everything at a glance and activate the boutique.',
      'Progress is auto-saved as a draft in Firestore — business owners can close the browser and come back later.',
      'Business users are automatically redirected to onboarding if they haven\'t completed setup.',
    ],
    files: [
      'src/pages/business/BoutiqueOnboardingPage.tsx (NEW)',
      'src/components/business/onboarding/ — 8 step components (NEW)',
      'src/hooks/useBoutiqueOnboarding.ts (NEW)',
      'src/types/business.ts (NEW)',
    ],
  },
  {
    id: 'analytics',
    icon: BarChart3,
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
    title: 'Business Analytics Dashboard',
    tag: 'new',
    area: 'Business',
    summary: 'A full analytics dashboard so business owners can see how their store is performing — revenue, orders, popular products, customer metrics, and more.',
    details: [
      'KPI cards show key metrics at a glance: total revenue, order count, average order value, conversion rate.',
      'Four sections: Overview, Orders, Products, and Customers — each with detailed breakdowns.',
      'Date range filters let you view data for the last 7 days, 30 days, 90 days, or 12 months.',
      'Trend indicators show whether metrics are going up or down compared to the previous period.',
      'Product performance shows best sellers, return rates, and view-to-purchase conversion.',
      'Customer metrics include repeat purchase rate, lifetime value, and satisfaction scores.',
    ],
    files: [
      'src/pages/business/BusinessAnalyticsDashboard.tsx (NEW)',
    ],
  },
  {
    id: 'admin',
    icon: Settings,
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    title: 'Admin Panel — Major Upgrade',
    tag: 'upgraded',
    area: 'Admin',
    summary: 'The admin business management page was completely rebuilt. It now uses real Firestore data instead of hardcoded mockups, and admins can actually edit boutiques and manage products.',
    details: [
      'Boutique list now loads from Firestore in real time — no more hardcoded fake data.',
      'Admins can inline-edit boutique details: name, description, address, hours, and more.',
      'Added a product management panel — view all products per boutique, toggle active/inactive, delete.',
      'Admins can feature/unfeature boutiques (featured ones appear on the homepage).',
      'Admins can change boutique status: active, pending, suspended.',
      'Added an audit log viewer showing a history of admin actions.',
      'All changes are written to Firestore immediately and reflected across all connected clients.',
    ],
    files: [
      'src/pages/admin/AdminBusinesses.tsx — major rewrite',
      'src/hooks/useAdminBoutiques.ts (NEW)',
      'src/hooks/useAdminProducts.ts (NEW)',
    ],
  },
  {
    id: 'homepage',
    icon: Smartphone,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    title: 'Home Page — Mobile Redesign',
    tag: 'improved',
    area: 'Shopper',
    summary: 'The home page was redesigned mobile-first. Smaller header, inline search bar, category filters, and a compact featured boutique banner that works great on phones.',
    details: [
      'Header is now more compact on mobile: smaller icons, tighter spacing, no wasted space.',
      'The text "Fash" title was replaced with the actual FASH brand logo image.',
      'The large hero section is now split: a slim banner on mobile vs. the full hero on desktop.',
      'An inline search bar in the mobile header opens a full-screen search overlay on tap — feels like a native app.',
      'Category filter pills (All, Dresses, Tops, Bottoms) let shoppers quickly filter trending products.',
      '"Show More" button on mobile — shows 4 products initially, tap to reveal the rest.',
    ],
    files: [
      'src/pages/shopper/HomePage.tsx',
      'src/constants/brand.ts (NEW)',
    ],
  },
  {
    id: 'checkout',
    icon: ShoppingBag,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    title: 'Bag & Checkout — Complete Rewrite',
    tag: 'upgraded',
    area: 'Shopper',
    summary: 'The bag page went from a static mockup to a fully working checkout. Enter your address, enter your card, authorize payment, and your order is created.',
    details: [
      'Two-step flow: Cart view (review items, enter address) then Payment view (enter card details).',
      'Delivery address form with street, city, state, and zip code fields.',
      'Stripe Elements card input — the card number field is provided by Stripe\'s secure iframe, so card details never touch our server.',
      'Delivery fee is estimated using the Uber Direct quote API based on the actual delivery distance.',
      'Payment uses "authorize only" — your card is held but not charged until the courier picks up.',
      'On success, a real order document is created in Firestore and the shopper is shown a confirmation.',
      'Terms of service agreement checkbox is required before proceeding.',
    ],
    files: [
      'src/pages/shopper/BagPage.tsx — complete rewrite (~672 lines)',
    ],
  },
  {
    id: 'tracking',
    icon: MapPin,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Order Tracking — Real Data',
    tag: 'upgraded',
    area: 'Shopper',
    summary: 'The order tracking page now shows real order data from Firestore instead of hardcoded mock data. It integrates with Uber Direct for live courier tracking.',
    details: [
      'Fetches real order data in real time using Firestore\'s onSnapshot listener.',
      'Shows the Uber courier\'s name, vehicle info, and live tracking URL when a driver is assigned.',
      'Displays the estimated time of arrival (ETA) from the Uber Direct API.',
      'Loading spinner while fetching, and a friendly error page if the order isn\'t found.',
      'Manual "refresh status" button lets shoppers poll for the latest delivery status.',
      'Shows a warning banner if the courier dispatch failed so the shopper knows what\'s happening.',
      'Delivery address displayed from the actual order data instead of a hardcoded string.',
    ],
    files: [
      'src/pages/shopper/OrderTrackingPage.tsx — rewritten',
      'src/hooks/useOrders.ts — added useOrder hook',
    ],
  },
  {
    id: 'boutiques-list',
    icon: Layers,
    iconBg: 'bg-fuchsia-100',
    iconColor: 'text-fuchsia-600',
    title: 'Boutiques List Page',
    tag: 'new',
    area: 'Shopper',
    summary: 'A new page where shoppers can browse all available boutiques on FASH, with search and filtering to find stores they\'re interested in.',
    details: [
      'Displays all boutiques in a visually rich card layout with logos, product previews, and delivery times.',
      'Search bar to filter boutiques by name.',
      'Category filters to find stores that carry specific types of fashion.',
      'Tapping a boutique card navigates to that store\'s full page.',
    ],
    files: [
      'src/pages/shopper/BoutiquesListPage.tsx (NEW)',
    ],
  },
  {
    id: 'products',
    icon: Tag,
    iconBg: 'bg-lime-100',
    iconColor: 'text-lime-600',
    title: 'DomDavis Product Catalog Expanded',
    tag: 'improved',
    area: 'Products',
    summary: 'Added 10+ new DomDavis products across three collections: Daily Dom, Timeless Denim, and UNTAMED. Each has real images, sizes, and descriptions.',
    details: [
      'Daily Dom Collection: Black and Gray long sleeve tops ($45 each) and matching pants ($65 each).',
      'Timeless Denim Collection: Denim Dream Jumpsuit ($190) and other statement denim pieces.',
      'UNTAMED Collection: Bold, fashion-forward pieces from DomDavis\'s flagship line.',
      'All products use real product images from the DomDavis official website.',
      'Every product has full size availability (XS through XL) and delivery time estimates.',
    ],
    files: [
      'src/pages/shopper/ProductDetailPage.tsx — ~230 lines of new products',
    ],
  },
  {
    id: 'routing',
    icon: Route,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Routing — 7 New Pages + Protection',
    tag: 'improved',
    area: 'App',
    summary: 'Added routes for all the new pages and improved access control. Business routes now require authentication and check onboarding status.',
    details: [
      'New routes: /boutiques, /search, /settings/notifications, /business/onboarding, /business/analytics, /admin/analytics, /changelog.',
      'Added a BusinessRoute wrapper that automatically redirects business users to onboarding if they haven\'t completed setup.',
      'Business routes now require proper authentication — previously they were open for anyone to see.',
      'Admin analytics page gets its own route with the admin layout wrapper.',
    ],
    files: [
      'src/router.tsx',
    ],
  },
  {
    id: 'firestore-rules',
    icon: Shield,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    title: 'Firestore Security Rules — Tightened',
    tag: 'security',
    area: 'Security',
    summary: 'Updated database security rules to support new features while keeping data locked down. Shoppers can now create orders, and several new collections have proper access control.',
    details: [
      'Orders: shoppers can now CREATE orders (needed for checkout) but still cannot update or delete them — only Cloud Functions can.',
      'Businesses: added a create rule so business users can set up their own business document during onboarding.',
      'Added a private subcollection (businesses/{id}/private/) for sensitive data like Stripe keys and tax IDs.',
      'Added FCM token subcollection rules — only Cloud Functions can write device tokens.',
      'Added returns collection rules — only viewable by the shopper, business, or admin involved.',
      'Added admin_logs collection — admins can read and create logs, but nobody can edit or delete them (immutable audit trail).',
    ],
    files: [
      'firestore.rules',
    ],
  },
  {
    id: 'indexes',
    icon: Database,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    title: 'Firestore Indexes — Expanded',
    tag: 'improved',
    area: 'Database',
    summary: 'Added ~10 new composite indexes so the new queries in the app actually work. Firestore requires these for any query that filters or sorts on multiple fields.',
    details: [
      'Shopper order queries: filter by shopperId + sort by date, filter by shopperId + state + date.',
      'Business order queries: filter by businessId + sort by date, filter by businessId + state.',
      'Driver delivery queries: filter by driverId + state + date.',
      'Admin queries: filter orders by state, by payment status.',
      'Inventory queries: filter by reservation state and expiration time (for cleanup).',
      'Without these indexes, Firestore would return errors on the new query patterns.',
    ],
    files: [
      'firestore.indexes.json',
    ],
  },
  {
    id: 'storage',
    icon: HardDrive,
    iconBg: 'bg-stone-100',
    iconColor: 'text-stone-600',
    title: 'Storage Rules — New Upload Paths',
    tag: 'improved',
    area: 'Storage',
    summary: 'Updated Firebase Storage rules to support business cover images and public brand assets.',
    details: [
      'Added business-covers/ path so business owners can upload cover images during onboarding.',
      'Added public read access for root-level files (FASH logo stored in Firebase Storage).',
      'Business owners can now upload both logos and cover images to their dedicated folders.',
    ],
    files: [
      'storage.rules',
    ],
  },
  {
    id: 'firebase-config',
    icon: Bell,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    title: 'Firebase Config — Push Notifications',
    tag: 'improved',
    area: 'Config',
    summary: 'Added Firebase Cloud Messaging (FCM) setup to the frontend config so the app can request push notification permissions and receive messages.',
    details: [
      'Added requestPushPermission() — asks the browser for notification permission and returns an FCM device token.',
      'Added onForegroundMessage() — listens for push messages while the app is open in the foreground.',
      'Added getPushPermissionStatus() — checks if notifications are granted, denied, or not yet asked.',
      'FCM is initialized lazily and only in supported browsers (gracefully skipped on unsupported ones).',
      'VAPID key configuration for web push certificates.',
    ],
    files: [
      'src/config/firebase.ts',
    ],
  },
  {
    id: 'misc',
    icon: Wrench,
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-500',
    title: 'Smaller Changes & Housekeeping',
    tag: 'fix',
    area: 'Various',
    summary: 'Dependency updates, new TypeScript types, documentation files, environment config, and minor UI tweaks across many files.',
    details: [
      'Added Stripe, Algolia, SendGrid, and Framer Motion to package.json dependencies.',
      'Created .env.example documenting all environment variables needed to run the project.',
      'Created BACKEND_ARCHITECTURE.md and IMPLEMENTATION_ROADMAP.md planning documents.',
      'Created visual backend architecture diagrams (BACKEND_SPEC_VISUAL.html).',
      'Added TypeScript type definitions for business, order, and product data in src/types/.',
      'Minor styling improvements to ProductCard, BoutiquePage, sign-in/sign-up pages, and SettingsPage.',
      'Updated the database ID in Firebase admin config to point to the correct Firestore database.',
      'Added test scripts for the inventory system and Algolia setup.',
    ],
    files: [
      'package.json, functions/package.json',
      '.env.example (NEW)',
      'BACKEND_ARCHITECTURE.md (NEW)',
      'src/types/ (NEW)',
      'Various component files',
    ],
  },
];

// ─── Area filters ───────────────────────────────────────────

const areas = ['All', ...Array.from(new Set(changelog.map((c) => c.area)))];

// ─── Single Accordion Item ──────────────────────────────────

function ChangelogItem({ entry, isOpen, toggle }: { entry: ChangelogEntry; isOpen: boolean; toggle: () => void }) {
  const Icon = entry.icon;
  const tag = tagStyles[entry.tag];

  return (
    <div className={cn(
      'rounded-2xl border transition-all duration-300',
      isOpen ? 'border-primary/30 shadow-lg bg-white' : 'border-gray-100 shadow-soft bg-white hover:border-gray-200 hover:shadow-md',
    )}>
      {/* Header — always visible */}
      <button
        onClick={toggle}
        className="w-full flex items-start gap-4 p-5 md:p-6 text-left cursor-pointer group"
      >
        {/* Icon */}
        <div className={cn('flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center', entry.iconBg)}>
          <Icon className={cn('h-5 w-5', entry.iconColor)} />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base md:text-lg font-semibold text-heading leading-tight">{entry.title}</h3>
            <span className={cn('text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full', tag.bg, tag.text)}>
              {tag.label}
            </span>
          </div>
          <p className="text-sm text-body leading-relaxed line-clamp-2">{entry.summary}</p>
        </div>

        {/* Chevron */}
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="flex-shrink-0 mt-1"
        >
          <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
        </motion.div>
      </button>

      {/* Expandable details */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-6 pt-0">
              {/* Divider */}
              <div className="border-t border-gray-100 mb-5" />

              {/* Detail bullets */}
              <div className="space-y-3 mb-6">
                {entry.details.map((detail, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex-shrink-0 mt-1.5">
                      <div className={cn('w-2 h-2 rounded-full', entry.iconBg.replace('100', '400'))} />
                    </div>
                    <p className="text-sm text-heading/80 leading-relaxed">{detail}</p>
                  </div>
                ))}
              </div>

              {/* Files */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-body uppercase tracking-wider mb-2">Files Changed</p>
                <div className="space-y-1">
                  {entry.files.map((file, i) => (
                    <p key={i} className="text-xs text-body font-mono leading-relaxed">{file}</p>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Stats bar ──────────────────────────────────────────────

function StatsBar() {
  const stats = [
    { label: 'Files Changed', value: '84' },
    { label: 'Lines Added', value: '6,197' },
    { label: 'New Features', value: '9' },
    { label: 'Upgrades', value: '4' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
      {stats.map((s) => (
        <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-soft p-4 text-center">
          <p className="text-2xl md:text-3xl font-serif font-bold text-heading">{s.value}</p>
          <p className="text-xs text-body mt-1">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export function ChangelogPage() {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('All');

  const toggle = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    if (openItems.size === filtered.length) {
      setOpenItems(new Set());
    } else {
      setOpenItems(new Set(filtered.map((c) => c.id)));
    }
  };

  const filtered = filter === 'All' ? changelog : changelog.filter((c) => c.area === filter);
  const allOpen = openItems.size === filtered.length && filtered.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BackButton
              fallback={SHOPPER_HOME}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              iconClassName="h-5 w-5 text-heading"
            />
            <Link to={SHOPPER_HOME}>
              <img src={FASH_LOGO} alt="Fash" className="h-10 object-contain" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to={SHOPPER_HOME} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Home">
              <Home className="h-5 w-5 text-heading" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Title */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-5 w-5 text-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Changelog</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-heading mb-3">
            What's New in FASH
          </h1>
          <p className="text-body leading-relaxed">
            Every change made since January 5, 2026 — explained in plain English.
            Tap any item to see the full details.
          </p>
          <p className="text-xs text-body/60 mt-2">Last updated: February 7, 2026</p>
        </div>

        {/* Stats */}
        <StatsBar />

        {/* Filters + Expand All */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          {/* Area filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            {areas.map((area) => (
              <button
                key={area}
                onClick={() => setFilter(area)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all',
                  filter === area
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-gray-100 text-body hover:bg-gray-200',
                )}
              >
                {area}
              </button>
            ))}
          </div>

          {/* Expand / Collapse all */}
          <button
            onClick={expandAll}
            className="text-xs font-medium text-primary hover:text-primary-hover transition-colors whitespace-nowrap self-end sm:self-auto"
          >
            {allOpen ? 'Collapse All' : 'Expand All'}
          </button>
        </div>

        {/* Changelog list */}
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map((entry) => (
              <motion.div
                key={entry.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <ChangelogItem
                  entry={entry}
                  isOpen={openItems.has(entry.id)}
                  toggle={() => toggle(entry.id)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Footer note */}
        <div className="mt-12 text-center text-xs text-body/50">
          <p>All changes are uncommitted and pending review.</p>
          <p className="mt-1">84 files changed &middot; 6,197 additions &middot; 840 deletions</p>
        </div>
      </main>
    </div>
  );
}
